import orderBy from 'lodash/orderBy'
// @ts-ignore
import filterDeep from 'deepdash/filterDeep'
// @ts-ignore
import mapValuesDeep from 'deepdash/mapValuesDeep'
// @ts-ignore
import mapDeep from 'deepdash/mapDeep'
// @ts-ignore
import findValueDeep from 'deepdash/findValueDeep'
// @ts-ignore
import findPathDeep from 'deepdash/findPathDeep'
// @ts-ignore
import pathToString from 'deepdash/pathToString'
import has from 'lodash/has'
import get from 'lodash/get'
import set from 'lodash/set'
import isPlainObject from 'lodash/isPlainObject'
import cloneDeep from 'lodash/cloneDeep'
import LocalizationService from '@/services/localization-service'

/**
 * Static Service class to handle different task on MyMenu
 */
class MyMenuService {
  /**
   * Sort menu categories and items by their sort field (ascending).
   */
  static sortMenuBySort(menu: Array<any>): Array<any> {
    if (!Array.isArray(menu)) return menu

    return orderBy(menu, [(node: any) => Number(node.sort) || 0], ['asc']).map((category: any) => ({
      ...category,
      subcategories: MyMenuService.sortMenuBySort(category.subcategories ?? []),
      items: orderBy(category.items ?? [], [(item: any) => Number(item.sort) || 0], ['asc']),
    }))
  }

  /**
   * Handle filtering, sorting and searching for menu
   *
   *
   * @param originalMenu menu without any modifications
   * @param {('az' | 'za' | 'newest' | 'oldest')} sort
   * @param {string} query
   * @param {Array} filters
   */
  static async processMenu(originalMenu: Array<any>, { sort, query, filters }: any, currentBranchId: any = null) {
    return new Promise(resolve => {
      const search = (query || '').toLowerCase()

      if (search.length === 1) {
        return resolve([])
      }

      if (search.length <= 1 && !filters.length && (!sort || sort === 'original')) {
        return resolve(MyMenuService.sortMenuBySort(Array.isArray(originalMenu) ? originalMenu : Object.values(originalMenu ?? {})))
      }

      let menu = { ...originalMenu }

      if (search.length > 1) {
        menu = filterDeep(menu, (value: any) => {
          // Combine search logic for name, description, and barcode
          const searchMatches = (qr: string) => (((value.locales ?? value.locale ?? []).some((locale: any) => (
                      locale.name?.toLowerCase()?.indexOf(qr) > -1
                      || locale.description?.toLowerCase()?.indexOf(qr) > -1
          )) ?? false)
                  || ((value?.price_labels ?? []).some((label: any) => label?.barcode?.toLowerCase().indexOf(qr) > -1) ?? false))

          // Apply search logic to locales and price_labels
          return searchMatches(search.toLowerCase())
        }, {
          childrenPath: ['subcategories', 'items'],
          rootIsChildren: true,
          onTrue: {
            skipChildren: true,
            cloneDeep: true,
            keepIfEmpty: true,
          },
        })
      }

      if (filters.length) {
        menu = mapValuesDeep(menu, (value: any) => ({ ...(value ?? {}), originalSubcategoriesCount: (value?.subcategories ?? [])?.length, originalItemsCount: (value?.items ?? [])?.length }), {
          callbackAfterIterate: false,
          childrenPath: ['subcategories'],
          rootIsChildren: true,
        })

        menu = filterDeep(menu, (value: any) => filters.every((filter: string) => {
          const hasChildren = value.items?.length || value.subcategories?.length
          const isCategory = has(value, 'parent_id')
          let currentBranch = null
          if (value.branches?.length > 0) {
            currentBranch = value.branches.find((branch: any) => branch.id === currentBranchId)
          }
          switch (filter) {
            case 'with-image':
              return (isCategory && !value.has_image && hasChildren) ? undefined : value.has_image
            case 'without-image':
              return (isCategory && value.has_image && hasChildren) ? undefined : !value.has_image
            case 'hidden-items':
              return (isCategory && hasChildren) ? !value.visible : !value.visible
            case 'without-price':
              return value.is_category ? undefined : !(value.price_labels?.length || value.open_price || value.price)
            case 'out-of-stock':
              return value.is_category ? undefined : (value.out_stock)
            case 'pinned':
              return value.is_category ? Object.values(value.pin.schedule).some((v: any) => v.closed === false) : undefined
            case 'controlled-availability':
              return value.is_category ? Object.values(value.available.schedule).some((v: any) => v.closed === false) : undefined
            case 'without-translation':
              return value.locale?.length < 2
            case 'without-description':
              return value.locales?.some((l: any) => l.description === null || l.description === '')
            default:
              return currentBranch?.[filter]
          }
        }), {
          childrenPath: ['subcategories', 'items'],
          rootIsChildren: true,
        })
      }

      if (sort && sort !== 'original') {
        let field: any = 'name'
        let orderType: 'asc' | 'desc' = 'asc'
        switch (sort) {
          case 'az': field = 'name'; orderType = 'asc'; break
          case 'za': field = 'name'; orderType = 'desc'; break
          case 'newest': field = 'created_at'; orderType = 'desc'; break
          case 'oldest': field = 'created_at'; orderType = 'asc'; break
          default: break
        }
        menu = orderBy(menu ?? [], field, orderType)
        menu = mapValuesDeep(menu, (value: any, key: any, parentValue: any, context: any) => {
          if (!context.afterIterate) return value
          return ({ ...(value ?? {}), subcategories: orderBy(value?.subcategories ?? [], field, orderType), items: orderBy(value?.items ?? [], field, orderType) })
        }, {
          callbackAfterIterate: true,
          childrenPath: ['subcategories'],
          rootIsChildren: true,
        })
      }

      return resolve(Object.values(menu ?? {}).filter((category: any) => isPlainObject(category)))
    })
  }

  /**
   * Recursion function to get a flatted array of menu for categories only
   *
   *
   * @param {Array} categories menu
   * @param {Object} parent for recursion
   * @param {number} depth for recursion
   */
  static flatCategories(categories: any, parent?: any, depth = 0): any {
    if (!Array.isArray(categories)) return categories

    let parentName = parent?.name
    if (parent?.parent_name) {
      parentName = `${parent?.parent_name} / ${parentName}`
    }

    return categories.reduce((acc: any, el: any) => {
      const category = {
        ...el, parent_name: parentName, has_items: !!el.items?.length, has_subcategories: !!el.subcategories?.length, level: depth,
      }
      return acc.concat(el.subcategories?.length ? [category, ...MyMenuService.flatCategories(el.subcategories, el, depth + 1)] : [category])
    }, [])
  }

  /**
   * Get a flatted array of all items of specific categories
   *
   * @param {Array} categories
   */
  static flatItems(categories: any): any {
    return mapDeep(categories, (item: any) => item, { leavesOnly: true, childrenPath: ['subcategories', 'items'], rootIsChildren: true }).filter((item: any) => !item?.is_category)
  }

  /**
   * Remove specific category/item from menu
   *
   * @param {Array} categories the menu
   * @param {number} entityId category/item id
   * @param {('category' | 'item')} type node type
   */
  static remove(categories: Array<any>, entityId: number, type: 'category' | 'item') {
    const node = MyMenuService.getNode(categories, entityId, type)
    const parentId = node?.parent_id ?? node?.category_id

    const filtered = filterDeep(categories, (value: any) => {
      if (type === 'category') {
        return value.is_category ? value.id !== entityId : undefined
      }
      return !value.is_category ? value.id !== entityId : true
    }, {
      childrenPath: type === 'category' ? ['subcategories'] : ['subcategories', 'items'],
      rootIsChildren: true,
      onFalse: {
        skipChildren: true,
        keepIfEmpty: false,
      },
    })

    // update parents children counts
    const currentItemsCount = node?.items_count || 0
    const currentCategoriesCount = node?.child_count || 1
    const itemsDifference = type === 'item' ? -1 : -1 * currentItemsCount
    const categoriesDifference = type === 'category' ? -1 * currentCategoriesCount : 0
    const newCategories = MyMenuService.updateParentChildrenCounts(filtered, parentId, itemsDifference, categoriesDifference)

    return isPlainObject(newCategories) ? Object.values(newCategories) : newCategories
  }

  /**
   * Returns a category/item from menu
   *
   *
   * @param {Array} categories - the menu
   * @param {number} entityId - category/item id
   * @param {('category' | 'item')} type - node type
   */
  static getNode(categories: Array<any>, entityId: number, type: string) {
    const node = findValueDeep(categories, (value: any) => (type === 'category' && value.is_category && value.id === entityId) || (type === 'item' && !value.is_category && value.id === entityId), { rootIsChildren: true, childrenPath: type === 'category' ? ['subcategories'] : ['subcategories', 'items'] })
    return node ? cloneDeep(node) : null
  }

  /**
   * Recursion function to update parent children counts when move/copy/delete operations
   *
   *
   * @param {Array} categories - the menu
   * @param {number} categoryId - parent category Id to be updated
   * @param {number} itemsDifference - difference (negative or positive) to be added to items_count
   * @param {number} categoriesDifference - difference (negative or positive) to be added to child_count
   */
  static updateParentChildrenCounts(categories: Array<any>, categoryId: any, itemsDifference: number, categoriesDifference: number) {
    if (!categoryId) return categories

    const iterationConfig = { rootIsChildren: true, childrenPath: ['subcategories'] }
    const parentNodePath = findPathDeep(categories, (value: any) => (value.id === categoryId), iterationConfig)
    if (!parentNodePath) return categories
    // @ts-ignore
    const parentPath = pathToString(parentNodePath)
    const newCategories = cloneDeep(categories)

    const paths = parentPath.split('.')

    paths.reduce((prev: any, current: any) => {
      if (!prev) return current
      const itemsCount = get(categories, `${prev}.items_count`, 0)
      set(newCategories, `${prev}.items_count`, Math.max(itemsCount + itemsDifference, 0))
      const categoriesCount = get(categories, `${prev}.child_count`, 0)
      set(newCategories, `${prev}.child_count`, Math.max(categoriesCount + categoriesDifference, 0))
      return `${prev}.${current}`
    }, '')

    const itemsCount = get(categories, `${parentPath}.items_count`, 0)
    set(newCategories, `${parentPath}.items_count`, Math.max(itemsCount + itemsDifference, 0))
    const categoriesCount = get(categories, `${parentPath}.child_count`, 0)
    set(newCategories, `${parentPath}.child_count`, Math.max(categoriesCount + categoriesDifference, 0))

    return newCategories
  }

  /**
   * Insert item/category to the menu
   *
   *
   * @param {Array} menu
   * @param {number} parentId
   * @param {Object} entity - item/category
   * @param {('category' | 'item')} type
   * @param {number} index - specific index to insert to the parent children array
   */
  static insertNode(menu: Array<any>, parentId: number | null, entity: any, type: string, index = -1) {
    let categories = menu

    if (!parentId) {
      categories = isPlainObject(categories) ? Object.values(categories) : categories
      const pushedIndex = Math.min(categories.length, (index > -1 ? index : categories.length))
      categories.splice(pushedIndex, 0, entity)
      return categories
    }

    const iterationConfig = { rootIsChildren: true, childrenPath: ['subcategories'] }
    // @ts-ignore
    const parentPath = pathToString(findPathDeep(categories, value => value.id === parentId, iterationConfig))
    if (type === 'category') {
      const subcategories = get(categories, `${parentPath}.subcategories`, [])
      const pushedIndex = Math.min(subcategories.length, (index > -1 ? index : subcategories.length))
      subcategories.splice(pushedIndex, 0, entity)
      set(categories, `${parentPath}.subcategories`, subcategories)
    } else {
      const items = get(categories, `${parentPath}.items`, [])
      const pushedIndex = Math.min(items.length, (index > -1 ? index : items.length))
      items.splice(pushedIndex, 0, entity)
      set(categories, `${parentPath}.items`, items)
    }

    // update parents children counts
    const currentItemsCount = entity?.items_count || 0
    const currentCategoriesCount = entity?.child_count || 1
    const itemsDifference = type === 'item' ? 1 : currentItemsCount
    const categoriesDifference = type === 'category' ? currentCategoriesCount : 0
    const newCategories = MyMenuService.updateParentChildrenCounts(categories, parentId, itemsDifference, categoriesDifference)

    return isPlainObject(newCategories) ? Object.values(newCategories) : newCategories
  }

  /**
   * Move item/category to another parent
   *
   *
   * @param {Array} categories - the menu
   * @param {number} parentId
   * @param {Object} entity - item/category
   * @param {('category' | 'item')} type
   * @param {number} destIndex - specific index to insert to the new parent children array
   */
  static move(categories: Array<any>, parentId: number | null, entity: any, type: 'category' | 'item', destIndex = -1) {
    const newCategories = MyMenuService.remove(categories, entity.id, type)
    const movedItem = {
      ...entity,
      name: LocalizationService.getTranslate(entity, 'name'),
    }

    return MyMenuService.insertNode(newCategories, parentId, movedItem, type, destIndex)
  }

  /**
   * Copy item/category to another parent
   *
   *
   * @param {Array} categories - the menu
   * @param {number} parentId
   * @param {Object} entity - item/category
   * @param {string} newName - new name for item/category
   * @param {('category' | 'item')} type
   */
  static copy(categories: Array<any>, parentId: number | null, entity: any, newName: string, type: 'category' | 'item') {
    const copiedEntity = {
      ...entity,
      name: newName,
    }
    return MyMenuService.insertNode(categories, parentId, copiedEntity, type)
  }

  /**
   * Update specific item/category to a new one
   *
   *
   * @param {Array} categories - the menu
   * @param entity - item/category
   * @param {('category' | 'item')} type - node type
   */
  static update(categories: Array<any>, entity: any, type: 'category' | 'item') {
    const iterationConfig = { rootIsChildren: true, childrenPath: type === 'category' ? ['subcategories'] : ['subcategories', 'items'] }
    const entityId = entity.id
    const nodePath = findPathDeep(categories, (value: any) => (type === 'category' && value.is_category && value.id === entityId) || (type === 'item' && !value.is_category && value.id === entityId), iterationConfig)
    if (!nodePath) return categories
    // @ts-ignore
    const path = pathToString(nodePath)
    const newCategories = cloneDeep(categories)
    const oldEntity = get(newCategories, path, {})
    set(newCategories, path, { ...oldEntity, ...entity })
    return newCategories
  }

  /**
   * Add a new item/category to menu
   *
   *
   * @param {Array} categories - the menu
   * @param {Object} entity - item/category
   * @param {('category' | 'item')} type
   */
  static add(categories: Array<any>, entity: any, type: 'category' | 'item') {
    const iterationConfig = { rootIsChildren: true, childrenPath: ['subcategories'] }
    const parentId = entity.parent_id || entity.category_id
    if (!parentId) {
      if (type === 'item') {
        throw new Error('Add item to root is not allowed')
      } else {
        return [...(categories ?? []), entity]
      }
    }

    const parentNodePath = findPathDeep(categories, (value: any) => (value.id === parentId), iterationConfig)
    if (!parentNodePath) return categories
    // @ts-ignore
    const parentPath = pathToString(parentNodePath)
    const newCategories = cloneDeep(categories)

    if (type === 'category') {
      const subcategoriesLength = get(categories, `${parentPath}.subcategories.length`, 0)
      set(newCategories, `${parentPath}.subcategories[${subcategoriesLength}]`, entity)
    } else {
      const itemsLength = get(categories, `${parentPath}.items.length`, 0)
      set(newCategories, `${parentPath}.items[${itemsLength}]`, entity)
    }

    return MyMenuService.updateParentChildrenCounts(newCategories, parentId, type === 'item' ? 1 : 0, type === 'category' ? 1 : 0)
  }
}

export default MyMenuService
